/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CardNoteArchiveProps {
  id: string;
  content: string;
  noteTime: Date;
  authorId: string | null;
  isInternal: boolean;
  noteCategory: string | null;
  noteMetadataJson: string | null;
  isSystem: boolean;
  sentToCards: List<IdListItemProps> | null;
  receivedFromCardId: string | null;
  dateArchived: Date;
  cardId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CardNoteArchiveRecord extends Record<CardNoteArchiveProps>({
  id: null as any,
  content: null as any,
  noteTime: null as any,
  authorId: null as any,
  isInternal: null as any,
  noteCategory: null as any,
  noteMetadataJson: null as any,
  isSystem: null as any,
  sentToCards: null as any,
  receivedFromCardId: null as any,
  dateArchived: null as any,
  cardId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    isInternal: boolean;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    isSystem: boolean;
    sentToCards: any;
    receivedFromCardId: string | null;
    dateArchived: string | number | Date;
    cardId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CardNoteArchiveRecord {
    if (json == null) {
      throw new Error('CardNoteArchiveRecord.fromJS: parameter cannot be null');
    }
    return new CardNoteArchiveRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CardNoteArchiveJson.id',
      ),
      content: getFromOrThrow(json, (j) => j.content, 'CardNoteArchiveJson.content'),
      noteTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
          'CardNoteArchiveJson.noteTime',
        ),
      ),
      authorId: json.authorId,
      isInternal: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isInternal', false),
        'CardNoteArchiveJson.isInternal',
      ),
      noteCategory: json.noteCategory,
      noteMetadataJson: json.noteMetadataJson,
      isSystem: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSystem', false),
        'CardNoteArchiveJson.isSystem',
      ),
      sentToCards:
        json.sentToCards != null
          ? List<IdListItemRecord>(json.sentToCards.map((o: any) => new IdListItemRecord(o)))
          : null,
      receivedFromCardId: json.receivedFromCardId,
      dateArchived: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateArchived', '0001-01-01T00:00:00.00Z'),
          'CardNoteArchiveJson.dateArchived',
        ),
      ),
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'CardNoteArchiveJson.cardId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'CardNoteArchiveJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    isInternal: boolean;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    isSystem: boolean;
    sentToCards: any;
    receivedFromCardId: string | null;
    dateArchived: string | number | Date;
    cardId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CardNoteArchiveRecord, Error[]] {
    if (json == null) {
      throw new Error('CardNoteArchiveRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CardNoteArchiveRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CardNoteArchiveJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.content, 'CardNoteArchiveJson.content'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
              'CardNoteArchiveJson.noteTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isInternal: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isInternal', false),
            'CardNoteArchiveJson.isInternal',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteCategory: tryOrDefault(
        () => json.noteCategory,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteMetadataJson: tryOrDefault(
        () => json.noteMetadataJson,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSystem: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSystem', false),
            'CardNoteArchiveJson.isSystem',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sentToCards: tryOrDefault(
        () =>
          json.sentToCards != null
            ? List<IdListItemRecord>(json.sentToCards.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      receivedFromCardId: tryOrDefault(
        () => json.receivedFromCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateArchived: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateArchived', '0001-01-01T00:00:00.00Z'),
              'CardNoteArchiveJson.dateArchived',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'CardNoteArchiveJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'CardNoteArchiveJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CardNoteArchiveProps) {
    super(props);
    if (props == null) {
      throw new Error('CardNoteArchiveRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CardNoteArchiveViewModelBase implements CardNoteArchiveProps {
  protected readonly _props: CardNoteArchiveProps;
  private _json?: CardNoteArchiveProps;

  get id(): string {
    return this._props.id;
  }
  get content(): string {
    return this._props.content;
  }
  get noteTime(): Date {
    return this._props.noteTime;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get isInternal(): boolean {
    return this._props.isInternal;
  }
  get noteCategory(): string | null {
    return this._props.noteCategory;
  }
  get noteMetadataJson(): string | null {
    return this._props.noteMetadataJson;
  }
  get isSystem(): boolean {
    return this._props.isSystem;
  }
  get sentToCards(): List<IdListItemProps> | null {
    return this._props.sentToCards;
  }
  get receivedFromCardId(): string | null {
    return this._props.receivedFromCardId;
  }
  get dateArchived(): Date {
    return this._props.dateArchived;
  }
  get cardId(): string {
    return this._props.cardId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CardNoteArchiveProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        content: this._props.content,
        noteTime: this._props.noteTime,
        authorId: this._props.authorId,
        isInternal: this._props.isInternal,
        noteCategory: this._props.noteCategory,
        noteMetadataJson: this._props.noteMetadataJson,
        isSystem: this._props.isSystem,
        sentToCards: this._props.sentToCards,
        receivedFromCardId: this._props.receivedFromCardId,
        dateArchived: this._props.dateArchived,
        cardId: this._props.cardId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCardNoteArchiveEntities = (state: AppState) =>
  state.entityStores.cardNoteArchive.entities;

export const selectCardNoteArchiveById = (id: string) => (state: AppState) =>
  state.entityStores.cardNoteArchive.entities.get(id);

export const selectCardNoteArchiveMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cardNoteArchive.meta.get(id);

export const selectCardNoteArchiveAndMetadataById = (id: string) =>
  createSelector(
    selectCardNoteArchiveById(id),
    selectCardNoteArchiveMetadataById(id),
    (cardNoteArchive, meta) => ({ cardNoteArchive, meta }),
  );

export function selectCardNoteArchiveEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCardNoteArchiveEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CardNoteArchiveRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCardNoteArchiveEntitiesByContent(content: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByNoteTime(noteTime: Date) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.noteTime === noteTime).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByIsInternal(isInternal: boolean) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.isInternal === isInternal).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByNoteCategory(noteCategory: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.noteCategory === noteCategory).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByNoteMetadataJson(noteMetadataJson: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.noteMetadataJson === noteMetadataJson).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByIsSystem(isSystem: boolean) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.isSystem === isSystem).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByReceivedFromCardId(receivedFromCardId: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.receivedFromCardId === receivedFromCardId).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByDateArchived(dateArchived: Date) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.dateArchived === dateArchived).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByCardId(cardId: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectCardNoteArchiveEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllCardNoteArchiveEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
